import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Spinner } from 'components';
import { useWindowSize } from 'hooks';
import { Equipment, EquipmentCategory, EquipmentsTableFields } from 'types';

import { DEFAULT_EQUIPMENTS_FIELDS, MOBILE_EQUIPMENTS_FIELDS } from '../config';
import { initialWindowSizeProps } from '../constants';
import { EmptyState } from '../EmptyState';
import { ContainerW, TitleW } from '../styled';
import { Table } from '../Table';
import { useRegisteredEquipmentTable } from './hooks/useRegisteredEquipmentTable';
import { OnRegisteredClickT } from './types';

interface RegisteredEquipmentsContainerProps {
  isSwedish: boolean;
  onTrashIconClick: (equipment: Pick<Equipment, 'id' | 'name'>) => void;
  category: EquipmentCategory | null;
}

export function RegisteredEquipmentsContainer({
  isSwedish,
  onTrashIconClick,
  category,
}: RegisteredEquipmentsContainerProps): ReactElement {
  const { employeeId = '' } = useParams<string>();
  const { isLoading, equipmentList, handleViewEquipment } = useRegisteredEquipmentTable({
    category,
    userId: employeeId,
    onTrashIconClick,
    isSwedish,
  });
  const { isMobile } = useWindowSize(initialWindowSizeProps);
  const { t } = useTranslation();

  const webColumns = DEFAULT_EQUIPMENTS_FIELDS;
  const mobileColumns = MOBILE_EQUIPMENTS_FIELDS;

  return (
    <ContainerW
      isListData={!!equipmentList.length}
      style={{ marginTop: '24px', paddingTop: '30px' }}
    >
      <TitleW>{t('equipments.register.title')}</TitleW>

      {isLoading && <Spinner />}

      {!isLoading && !equipmentList.length && <EmptyState />}

      {!isLoading && !!equipmentList.length && (
        <Table<EquipmentsTableFields, OnRegisteredClickT>
          isLoading={isLoading}
          columns={isMobile ? mobileColumns : webColumns}
          data={equipmentList}
          onClickRow={handleViewEquipment}
        />
      )}
    </ContainerW>
  );
}
